'use client';

import React, { useEffect, useState } from 'react';
import { Select, Text, Loader } from '@mantine/core';
import { useAuth } from '../hooks/useAuth';
import { getUserOrganizations, getUserProfile, setCurrentOrganization } from '../services/firestore';
import { Organization, OrganizationSelectorProps } from '../types';

export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ onSelect }) => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!user) return;
      
      try {
        const orgs = await getUserOrganizations(user.uid);
        setOrganizations(orgs);
        
        // Set current org if available
        const userProfile = await getUserProfile(user.uid);
        if (userProfile?.currentOrganization) {
          setSelectedOrg(userProfile.currentOrganization);
        } else if (orgs.length > 0) {
          setSelectedOrg(orgs[0].id);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrgs();
  }, [user]);

  const handleOrgChange = async (orgId: string) => {
    if (!user) return;
    
    setSelectedOrg(orgId);
    await setCurrentOrganization(user.uid, orgId);
    onSelect?.(orgId);
  };

  if (loading) {
    return <Loader size="sm" />;
  }

  if (organizations.length === 0) {
    return (
      <Text c="dimmed">No organizations. Create one to get started.</Text>
    );
  }

  return (
    <Select
      label="Current Organization"
      placeholder="Select organization"
      data={organizations.map(org => ({ value: org.id, label: org.name }))}
      value={selectedOrg}
      onChange={(value) => value && handleOrgChange(value)}
    />
  );
}; 